import { useEffect, useRef, useState } from "react";
import { processSteps } from "@/mocks/content";

export default function ProcessSection() {
  const sectionRef = useRef<HTMLElement>(null);
  const [visible, setVisible] = useState(false);
  const [parallaxY, setParallaxY] = useState<number[]>([0, 0, 0, 0]);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => { if (entry.isIntersecting) setVisible(true); },
      { threshold: 0.1 }
    );
    if (sectionRef.current) observer.observe(sectionRef.current);
    return () => observer.disconnect();
  }, []);

  useEffect(() => {
    const onScroll = () => {
      if (!sectionRef.current) return;
      const rect = sectionRef.current.getBoundingClientRect();
      const progress = -rect.top / window.innerHeight;
      setParallaxY([
        progress * -20,
        progress * -35,
        progress * -50,
        progress * -65,
      ]);
    };
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  return (
    <section
      id="process"
      ref={sectionRef}
      className="relative py-24 md:py-40 bg-[#FAFAF8] overflow-hidden"
    >
      {/* Diagonal Edge Background */}
      <div
        className="absolute inset-0 pointer-events-none"
        style={{
          background: "linear-gradient(135deg, #FAFAF8 50%, #F4F2EE 50%)",
        }}
      />

      <div className="relative z-10 max-w-7xl mx-auto px-6 md:px-16">
        {/* Header */}
        <div className="mb-16 md:mb-24">
          <div
            className="h-px bg-[#8B7355] mb-8 origin-left"
            style={{
              transform: visible ? "scaleX(1)" : "scaleX(0)",
              transition: "transform 0.8s cubic-bezier(0.77, 0, 0.175, 1)",
            }}
          />
          <p
            className="text-[#8B7355] text-xs tracking-[0.4em] mb-3 font-light"
            style={{
              fontFamily: "'Noto Serif JP', serif",
              opacity: visible ? 1 : 0,
              transition: "opacity 0.6s ease 0.2s",
            }}
          >
            HOW IT WORKS
          </p>
          <h2
            className="text-[#2C2C2C] font-light leading-tight"
            style={{
              fontFamily: "'Noto Serif JP', serif",
              fontSize: "clamp(1.8rem, 3.5vw, 3rem)",
              opacity: visible ? 1 : 0,
              transform: visible ? "translateY(0)" : "translateY(20px)",
              transition: "opacity 0.7s ease 0.3s, transform 0.7s ease 0.3s",
            }}
          >
            施術の流れ
          </h2>
        </div>

        {/* Steps — Overlap Parallax */}
        <div className="relative">
          {processSteps.map((step, i) => (
            <div
              key={step.step}
              className="relative mb-8 md:mb-0"
              style={{
                transform: `translateY(${parallaxY[i]}px)`,
                transition: "transform 0.1s linear",
                zIndex: i + 1,
              }}
            >
              <div
                className={`flex flex-col md:flex-row items-start gap-6 md:gap-12 p-8 md:p-12 ${
                  i % 2 === 0 ? "md:ml-0 md:mr-auto md:max-w-3xl" : "md:ml-auto md:mr-0 md:max-w-3xl"
                }`}
                style={{
                  background: i % 2 === 0 ? "#FAFAF8" : "#2C2C2C",
                  opacity: visible ? 1 : 0,
                  transform: visible
                    ? `translateY(${parallaxY[i]}px)`
                    : `translateY(${60 + i * 20}px)`,
                  boxShadow: visible
                    ? `0 ${12 + i * 8}px ${40 + i * 20}px rgba(44,44,44,${0.08 + i * 0.04})`
                    : "none",
                  transition: `opacity 0.7s ease ${i * 0.15}s, box-shadow 0.7s ease ${i * 0.15}s`,
                }}
              >
                {/* Step Number */}
                <div className="relative flex-shrink-0">
                  <span
                    className="font-light select-none"
                    style={{
                      fontFamily: "'Noto Serif JP', serif",
                      fontSize: "clamp(4rem, 8vw, 7rem)",
                      color: i % 2 === 0 ? "#E8E6E1" : "#6A6A6A",
                      lineHeight: 1,
                    }}
                  >
                    {step.step}
                  </span>
                </div>

                {/* Content */}
                <div className="flex-1 pt-2">
                  <div
                    className="h-px mb-4 origin-left"
                    style={{
                      background: i % 2 === 0 ? "#8B7355" : "#8B7355",
                      transform: visible ? "scaleX(1)" : "scaleX(0)",
                      transition: `transform 0.6s ease ${0.3 + i * 0.15}s`,
                    }}
                  />
                  <h3
                    className="font-light mb-3 leading-snug"
                    style={{
                      fontFamily: "'Noto Serif JP', serif",
                      fontSize: "clamp(1.1rem, 2vw, 1.5rem)",
                      color: i % 2 === 0 ? "#2C2C2C" : "#FAFAF8",
                    }}
                  >
                    {step.title}
                  </h3>
                  <p
                    className="text-sm leading-relaxed font-light"
                    style={{
                      fontFamily: "'Noto Serif JP', serif",
                      color: i % 2 === 0 ? "#4A4A4A" : "#FAFAF8",
                      opacity: i % 2 === 0 ? 1 : 0.75,
                    }}
                  >
                    {step.description}
                  </p>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}
